import type { ChatUIMessage } from "@shaarp/common";
import { UIMessage } from "ai";

export const convertUIMessagePartsToText = (message: UIMessage) => {
  return message.parts
    .map((part) => (part.type === "text" ? part.text : ""))
    .join("");
};

/**
 * How a compaction boundary written before the metadata type existed announces
 * itself. The API writes the same prefix; it cannot be shared from
 * `@shaarp/common`, which is types-only and emits no runtime code.
 */
const COMPACTION_TEXT_PREFIX = "[Summary of previous conversation]";

/**
 * Whether a message is a compaction boundary, which the thread renders as
 * nothing at all: compaction is the app's own housekeeping and says nothing to
 * the user.
 *
 * Centralized because two surfaces read it and must agree — one hides the
 * message, the other steps over it to keep a run of clarifying questions
 * together, and a boundary hidden by one but not the other would break the run
 * it sits in.
 */
export function isCompactionMessage(message: ChatUIMessage): boolean {
  if (message.metadata?.type === "COMPACTION") return true;
  // The first text part, not the whole message flattened: this runs over every
  // message of the thread on every stream delta, and the prefix is at the
  // front of the first part by construction — the boundary is written as one
  // text part and nothing else. Concatenating the thread instead cost a full
  // rescan, and the allocation of a copy of it, thirty times a second.
  const firstText = message.parts.find((part) => part.type === "text");
  return firstText?.text?.startsWith(COMPACTION_TEXT_PREFIX) ?? false;
}

/**
 * Removes unrecognized `[@shaarp/...]` markers from assistant text, keeping the
 * ones the UI knows how to render: email, suggestion, quick-reply, company and cite.
 * Centralized so every render surface shares the same allowlist (adding a new
 * marker type means updating this one place).
 */
export function stripUnrecognizedShaarpTags(text: string): string {
  return text.replace(
    /\[@shaarp\/(?!suggestion\s|quick-reply=|email(?:\s|])|company\s|cite\s)[^\]]*\]/g,
    ""
  );
}

/**
 * The text without its email blocks. An email's body renders as plain text,
 * its citation markers stripped, so whatever counts the pills of an answer
 * reads around them.
 */
export function stripEmailBlocks(text: string): string {
  return text.replace(
    /\[@shaarp\/email[^\]]*\][\s\S]*?\[@\/shaarp\/email\]/g,
    ""
  );
}
